import { baseApi } from "@/shared/api/baseApi";
import { RolesType, UserRole } from "@/entities/user";
import { User } from "../model/authTypes";

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface GetUserResponse {
  user: User;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const normalizeRoles = (raw: unknown): RolesType[] | undefined => {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const roles = list
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }
      if (isRecord(item) && typeof item.name === "string") {
        return item.name;
      }
      return "";
    })
    .filter(
      (role): role is RolesType =>
        role === UserRole.ADMIN ||
        role === UserRole.TEACHER ||
        role === UserRole.STUDENT,
    );

  return roles.length ? roles : undefined;
};

// Логин отдаёт { user }, а GET /api/auth/user — самого пользователя
const normalizeCurrentUser = (response: unknown): GetUserResponse => {
  if (!isRecord(response)) {
    throw new Error("Пустой ответ пользователя");
  }

  const source = isRecord(response.user) ? response.user : response;

  if (source.email == null && source.id == null) {
    throw new Error("Не удалось прочитать пользователя");
  }

  return {
    user: {
      id: String(source.id ?? ""),
      username: String(source.username ?? source.name ?? ""),
      email: String(source.email ?? ""),
      roles: normalizeRoles(source.roles),
    },
  };
};

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: ({ email, password }) => ({
        url: `/api/auth/login`,
        method: "POST",
        body: {
          email,
          password,
        },
      }),
      invalidatesTags: ["User"],
    }),
    getCurrentUser: builder.query<GetUserResponse, void>({
      query: () => ({
        url: "/api/auth/user",
        method: "GET",
      }),
      transformResponse: normalizeCurrentUser,
      providesTags: ["User"],
    }),
  }),
});

export const { useLoginMutation, useGetCurrentUserQuery } = authApi;
